import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
    TablePagination
} from '@mui/material';
import {
    Building2,
    BarChart3,
    RefreshCw,
    Folder,
    ArrowLeft
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer
} from 'recharts';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PMCostCenterDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [costCenters, setCostCenters] = useState([]);
    const [selectedCostCenterId, setSelectedCostCenterId] = useState('all');

    // Page view state: 'list' or 'detail'
    const [viewMode, setViewMode] = useState('list');
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [currencySymbol, setCurrencySymbol] = useState('₹');
    const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

    useEffect(() => {
        const fetchCurrency = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const company_id = payload.company_id;
                    const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (cmpRes.data.success && cmpRes.data.data) {
                        if (cmpRes.data.data.currency_symbol) {
                            setCurrencySymbol(cmpRes.data.data.currency_symbol);
                        }
                        setCompanyConfig({
                            dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                            timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
                        });
                    }
                } catch (e) {
                    // console.error("Failed to fetch company country", e);
                }
            }
        };
        fetchCurrency();
    }, []);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // Fetch only the detailed list of cost centers (active ones) with pagination
            const res = await axios.get(`${API_URL}/api/cost-center-dashboard/detailed-list?page=${page + 1}&limit=${rowsPerPage}&search=${searchQuery}`, { headers });
            if (res.data.success) {
                setCostCenters(res.data.data.list || []);
                setTotalCount(res.data.data.pagination?.totalItems || 0);
            }
        } catch (err) {
            //console.error('Error loading PM dashboard data:', err);
            setError(err.response?.data?.message || 'Failed to load PM financial dashboard analytics.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [page, rowsPerPage, searchQuery]);

    const handleOpenDetailView = async (costCenterId) => {
        try {
            setDetailLoading(true);
            setViewMode('detail');
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.get(`${API_URL}/api/cost-center-dashboard/detail/${costCenterId}`, { headers });
            if (res.data.success) {
                setDetailData(res.data.data);
            }
        } catch (err) {
            //console.error('Error loading detail report for PM:', err);
            alert('Failed to load detailed report for this Cost Center.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetailView = () => {
        setViewMode('list');
        setDetailData(null);
    };

    const formatCurrency = (amount) => {
        return `${currencySymbol}${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const formatCurrencyForPDF = (amount) => {
        return `${currencySymbol} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const handleExportExcel = () => {
        const exportData = filteredCostCenters.map(cc => ({
            'Cost Center Name': cc.name,
            'Cost Center Code': cc.code,
            'Total Projects': cc.projectsCount || 0,
            'Total Utilised ': cc.totalExpenditure
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PM Cost Centers Summary");
        XLSX.writeFile(workbook, `PM_Cost_Center_Projects_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF('portrait');
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(2, 136, 209);
        doc.text('Project Manager Cost Center Projects Summary', 14, 15);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on: ${dayjs().tz(companyConfig.timezone).format(companyConfig.dateFormat)}`, 14, 20);
        
        const headers = [[
            'Cost Center Name', 
            'Code', 
            'Total Projects', 
            'Total Utilised'
        ]];
        
        const rows = filteredCostCenters.map(cc => [
            cc.name,
            cc.code,
            cc.projectsCount || 0,
            formatCurrencyForPDF(cc.totalExpenditure)
        ]);
        
        doc.autoTable({
            startY: 25,
            head: headers,
            body: rows,
            theme: 'grid',
            headStyles: { fillColor: [2, 136, 209], textColor: [255, 255, 255], fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 4 },
            columnStyles: {
                0: { cellWidth: 70 },
                1: { cellWidth: 30 },
                2: { halign: 'center', cellWidth: 30 },
                3: { halign: 'right', fontStyle: 'bold' }
            }
        });
        
        doc.save(`PM_Cost_Center_Projects_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const filteredCostCenters = selectedCostCenterId === 'all'
        ? costCenters
        : costCenters.filter(c => c.id === Number(selectedCostCenterId));

    const totalCompanySpent = costCenters.reduce((sum, c) => sum + (c.totalExpenditure || 0), 0) || 1;

    // Calculate PM Metrics (Active Cost Centers, Total Projects, Total Budget)
    const activeCostCentersCount = filteredCostCenters.length;
    const totalProjectsCount = filteredCostCenters.reduce((sum, cc) => sum + (cc.projectsCount || 0), 0);
    const totalProjectsBudgetSum = filteredCostCenters.reduce((sum, cc) => sum + (cc.allocatedBudget || 0), 0);

    // --- GRAPH: Bar Chart comparing project budgets per cost center ---
    const budgetChartData = filteredCostCenters.map(cc => ({
        name: cc.code,
        fullName: cc.name,
        allocatedBudget: cc.allocatedBudget || 0
    })).filter(d => d.allocatedBudget > 0);

    // Detail view specific calculations
    const detailProjectsBudgetSum = detailData?.projects?.reduce((sum, p) => sum + (Number(p.project_budget) || Number(p.budget) || 0), 0) || 0;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 10, minHeight: '80vh' }}>
                <CircularProgress size={50} sx={{ color: '#0288d1', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Loading Project Manager Dashboard...</Typography>
            </Box>
        );
    }

    // ===== RENDER DETAIL VIEW MODE (NEW SUB-PAGE STYLE WITH BACK ARROW) =====
    if (viewMode === 'detail') {
        return (
            <Box sx={{ p: 4,  minHeight: '100vh' }}>
                {/* Header Row with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Tooltip title="Back to Cost Centers List">
                        <IconButton 
                            onClick={handleCloseDetailView} 
                            sx={{ 
                                bgcolor: 'white', 
                                boxShadow: 2, 
                                '&:hover': { bgcolor: '#f0f4f8' },
                                p: 1.5 
                            }}
                        >
                            <ArrowLeft size={24} color="#0288d1" />
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800,  letterSpacing: '-0.5px' }}>
                            {detailData?.costCenter?.name || 'Cost Center'} ({detailData?.costCenter?.code || 'N/A'})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Cost Center Projects & Allocation Details
                        </Typography>
                    </Box>
                </Box>

                {detailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                        <CircularProgress size={45} />
                    </Box>
                ) : detailData ? (
                    <Box>
                        {/* Summary Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6}>
                                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: 'white', borderLeft: '5px solid #0288d1' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL PROJECTS</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0288d1', mt: 0.5 }}>
                                            {detailData.projects?.length || 0} Projects
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                               <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: 'white', borderLeft: '5px solid #0288d1' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" sx={{ opacity: 0.9 }} fontWeight={600}>TOTAL PROJECTS BUDGET</Typography>
                                       <Typography variant="h5" sx={{ fontWeight: 800, color: '#0288d1', mt: 0.5 }}>
                                            {formatCurrency(detailProjectsBudgetSum)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Project Budgets Breakdown Chart */}
                        {detailData.projects?.length > 0 && (
                            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                    <BarChart3 size={22} color="#0288d1" />
                                    <Typography variant="h6" fontWeight="bold" color="#0288d1">
                                        Project Budgets Breakdown
                                    </Typography>
                                </Box>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={detailData.projects.map(proj => ({
                                        name: proj.key || proj.project_name || proj.name,
                                        fullName: proj.project_name || proj.name,
                                        budget: Number(proj.project_budget) || Number(proj.budget) || 0
                                    }))}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <RechartsTooltip 
                                            formatter={(val) => formatCurrency(val)}
                                            labelFormatter={(label) => {
                                                const proj = detailData.projects.find(p => (p.key || p.project_name || p.name) === label);
                                                return proj ? (proj.project_name || proj.name) : label;
                                            }}
                                        />
                                        <Bar dataKey="budget" name="Project Budget" fill="#0288d1" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        )}

                        {/* Projects List */}
                        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Folder size={24} color="#0288d1" />
                                <Typography variant="h6" fontWeight="bold" color="#0288d1">
                                    Linked Projects List
                                </Typography>
                            </Box>
                            <TableContainer>
                                <Table size="medium">
                                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Project Key</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Project Budget</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailData.projects?.map((proj) => (
                                            <TableRow key={proj.project_id || proj.id}>
                                                <TableCell sx={{ fontWeight: 600, color: '#0288d1' }}>{proj.project_name || proj.name}</TableCell>
                                                <TableCell><Chip label={proj.key || 'N/A'} size="small" variant="outlined" /></TableCell>
                                                <TableCell>{proj.projectType || 'Standard'}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{formatCurrency(proj.project_budget || proj.budget)}</TableCell>
                                                <TableCell><Chip label={proj.status || 'Active'} color="success" size="small" /></TableCell>
                                            </TableRow>
                                        ))}
                                        {detailData.projects?.length === 0 && (
                                            <TableRow><TableCell colSpan={5} align="center">No projects linked to this Cost Center.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                ) : null}
            </Box>
        );
    }

    // ===== RENDER MAIN LIST VIEW MODE =====
    return (
        <Box sx={{ p: 4,  minHeight: '100vh' }}>
            {/* Top Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                    Cost Center Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Cost Center Project Dashboards & Allocated Project Budgets
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ 
                            bgcolor: '#0288d1', 
                            '&:hover': { bgcolor: '#01579b' }, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderRadius: 2,
                            px: 2.5
                        }}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PdfIcon />}
                        onClick={handleExportPDF}
                        sx={{ 
                            bgcolor: '#c62828', 
                            '&:hover': { bgcolor: '#b71c1c' }, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderRadius: 2,
                            px: 2.5
                        }}
                    >
                        Export
                    </Button>
                   
                    <TextField
                        select
                        size="small"
                        value={selectedCostCenterId}
                        onChange={(e) => setSelectedCostCenterId(e.target.value)}
                        sx={{ minWidth: 240, bgcolor: 'white', borderRadius: 1 }}
                        label="Filter Cost Center"
                    >
                        <MenuItem value="all">
                            <em>🏢 All Cost Centers ({costCenters.length})</em>
                        </MenuItem>
                        {costCenters.map((cc) => (
                            <MenuItem key={cc.id} value={cc.id}>
                                {cc.name} ({cc.code})
                            </MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title="Refresh Dashboard">
                        <IconButton onClick={fetchDashboardData} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                            <RefreshCw size={20} color="#0288d1" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

         

          

            {/* ===== COST CENTER TABLE ===== */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, mb: 4 }}>
                <Box sx={{ p: 2.5, bgcolor: '#0288d1', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Building2 size={24} />
                        <Typography variant="h6" fontWeight="bold">
                            Cost Center Projects List
                        </Typography>
                    </Box>
                    <Chip label="💡 Click any row for project details" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
                <TableContainer>
                    <Table sx={{ minWidth: 600 }}>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Cost Center Code & Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Projects</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Utilised</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCostCenters.map((cc) => {
                                const percentage = totalCompanySpent > 0 ? ((cc.totalExpenditure / totalCompanySpent) * 100) : 0;
                                let barColor = '#2e7d32'; // Green below 25%
                                if (percentage >= 75) {
                                    barColor = '#d32f2f'; // Red for >= 75%
                                } else if (percentage >= 50) {
                                    barColor = '#ed6c02'; // Orange for >= 50%
                                } else if (percentage >= 25) {
                                    barColor = '#0288d1'; // Blue for >= 25%
                                }

                                return (
                                    <TableRow
                                        key={cc.id}
                                        hover
                                        onClick={() => handleOpenDetailView(cc.id)}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f4f8' } }}
                                    >
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                                                {cc.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {cc.code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${cc.projectsCount || 0} Projects`} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a237e', fontSize: '0.95rem' }}>
                                                {formatCurrency(cc.totalExpenditure)}
                                            </Typography>
                                            <Box sx={{ width: '100%', mt: 0.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" fontWeight="bold" sx={{ color: barColor }}>
                                                        {percentage.toFixed(1)}% Share
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', mt: 0.2 }}>
                                                    <Box sx={{ 
                                                        width: `${Math.min(percentage, 100)}%`, 
                                                        height: '100%', 
                                                        bgcolor: barColor, 
                                                        borderRadius: 3 
                                                    }} />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Button size="small" variant="contained" sx={{ bgcolor: '#0288d1', textTransform: 'none', fontSize: '0.75rem' }}>
                                                View Projects
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredCostCenters.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No cost centers found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
};

export default PMCostCenterDashboard;
